import {HeadingNode, SerializedHeadingNode} from '@lexical/rich-text';
import {
  $applyNodeReplacement,
  $createTextNode,
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';

import {HeadingSettings} from './commands';
import styles from './EnhancedHeadingNode.module.css';
import {generateHeadingId} from './HeadingIdUtils';
import {
  registerHeadingNode,
  shouldBlockHeadingEdits,
  unregisterHeadingNode,
} from './HeadingNodeUtils';

export type SerializedEnhancedHeadingNode = Spread<
  {
    collapsed: boolean;
    settings: HeadingSettings;
    headingId: string;
  },
  SerializedHeadingNode
>;

export const SCOPED_HTML_HEADING_IMPORT_ATTR = 'data-scoped-html-heading-import';

export class EnhancedHeadingNode extends HeadingNode {
  __collapsed: boolean;
  __settings: HeadingSettings;
  __headingId: string;

  static getType(): string {
    return 'heading';
  }

  static clone(node: EnhancedHeadingNode): EnhancedHeadingNode {
    return new EnhancedHeadingNode(
      node.__tag,
      node.__collapsed,
      node.__settings,
      node.__headingId,
      node.__key
    );
  }

  constructor(
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
    collapsed: boolean = false,
    settings: HeadingSettings = {},
    headingId?: string,
    key?: NodeKey
  ) {
    super(tag, key);
    this.__collapsed = collapsed;
    this.__settings = settings;
    this.__headingId = headingId || generateHeadingId();
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);

    // Add data attributes for styling and functionality
    element.setAttribute('data-collapsed', this.__collapsed.toString());
    element.setAttribute('aria-expanded', (!this.__collapsed).toString());

    // Add unique heading ID
    element.setAttribute('data-heading-id', this.__headingId);

    // Add slug if present
    if (this.__settings.slug) {
      element.setAttribute('data-heading-slug', this.__settings.slug);
    }

    // Add CSS module class for styling
    element.classList.add(styles.heading);

    // Register this heading node in the registry
    registerHeadingNode(this.__headingId, this.getKey());

    // Check if we're in document mode and make text non-editable
    this.updateEditableState(element);

    return element;
  }

  updateDOM(prevNode: EnhancedHeadingNode, dom: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode as any, dom, config);

    // Update editable state
    this.updateEditableState(dom);

    // Update collapsed state
    if (prevNode.__collapsed !== this.__collapsed) {
      dom.setAttribute('data-collapsed', this.__collapsed.toString());
      dom.setAttribute('aria-expanded', (!this.__collapsed).toString());
      return true;
    }

    if (prevNode.__settings.slug !== this.__settings.slug) {
      if (this.__settings.slug) {
        dom.setAttribute('data-heading-slug', this.__settings.slug);
      } else {
        dom.removeAttribute('data-heading-slug');
      }
      return true;
    }

    return isUpdated;
  }

  private updateEditableState(dom: HTMLElement): void {
    const element = dom;

    // Plain rich-text editors still use EnhancedHeadingNode as the registered
    // heading class, but should inherit normal Lexical editability.
    if (!shouldBlockHeadingEdits()) {
      element.removeAttribute('contenteditable');
      element.style.userSelect = '';
      return;
    }

    // Make enhanced document headings non-editable outside template mode.
    element.setAttribute('contenteditable', 'false');
    element.style.userSelect = 'none';
  }

  // Getters and setters for collapsed state
  setCollapsed(collapsed: boolean): void {
    const writableNode = this.getWritable();
    writableNode.__collapsed = collapsed;
  }

  getCollapsed(): boolean {
    return this.getLatest().__collapsed;
  }

  toggleCollapsed(): void {
    this.setCollapsed(!this.getCollapsed());
  }

  // Getters and setters for settings
  setSettings(settings: HeadingSettings): void {
    const writableNode = this.getWritable();
    writableNode.__settings = {...writableNode.__settings, ...settings};
  }

  getSettings(): HeadingSettings {
    return this.getLatest().__settings;
  }

  updateSettings(updates: Partial<HeadingSettings>): void {
    const writableNode = this.getWritable();
    writableNode.__settings = {...writableNode.__settings, ...updates};
  }

  // Specific setting getters/setters
  setWordLimit(wordLimit?: number): void {
    this.updateSettings({wordLimit});
  }

  getWordLimit(): number | undefined {
    return this.getSettings().wordLimit;
  }

  setGuidance(guidance?: string): void {
    this.updateSettings({guidance});
  }

  getGuidance(): string | undefined {
    return this.getSettings().guidance;
  }

  setSlug(slug?: string): void {
    this.updateSettings({slug});
  }

  getSlug(): string | undefined {
    return this.getSettings().slug;
  }

  // Heading ID getter and setter
  getHeadingId(): string {
    return this.getLatest().__headingId;
  }

  setHeadingId(headingId: string): void {
    const writableNode = this.getWritable();
    writableNode.__headingId = headingId;
  }

  // Override remove to unregister from the registry and clean up decorators
  remove(): void {
    // Clean up all decorator nodes before removing
    this.cleanupDecorators();
    unregisterHeadingNode(this.__headingId);
    super.remove();
  }

  // Override replace to clean up decorators when node is replaced
  replace<N extends LexicalNode>(replacement: N, includeChildren?: boolean): N {
    // Clean up all decorator nodes before replacement
    this.cleanupDecorators();
    return super.replace(replacement, includeChildren);
  }

  // Clean up all decorator nodes and restore only text content
  private cleanupDecorators(): void {
    // Get all children
    const children = this.getChildren();

    // Extract and preserve text content and formatting
    const textContent = this.getTextContent();
    const format = this.getFormat();
    const style = this.getStyle();

    // Remove ALL children (including decorators and text)
    children.forEach((child) => child.remove());

    // Restore only the text content with formatting
    if (textContent.trim()) {
      const textNode = $createTextNode(textContent);
      textNode.setFormat(format);
      this.append(textNode);
    }

    // Preserve the node's style
    this.setStyle(style);
  }

  static importDOM(): DOMConversionMap {
    const map: DOMConversionMap = {};
    (['h1', 'h2'] as const).forEach((tag) => {
      map[tag] = () => {
        return {
          priority: 2,
          conversion: (): DOMConversionOutput => ({
            node: $createEnhancedHeadingNode(tag),
          }),
        };
      };
    });
    (['h3', 'h4'] as const).forEach((tag) => {
      map[tag] = (element) => {
        if (!element.hasAttribute(SCOPED_HTML_HEADING_IMPORT_ATTR)) {
          return null;
        }
        return {
          priority: 2,
          conversion: (): DOMConversionOutput => ({
            node: $createEnhancedHeadingNode(tag),
          }),
        };
      };
    });
    return map;
  }

  exportJSON(): SerializedEnhancedHeadingNode {
    return {
      ...super.exportJSON(),
      type: 'heading',
      collapsed: this.__collapsed,
      settings: this.__settings,
      headingId: this.__headingId,
    };
  }

  static importJSON(serializedNode: SerializedEnhancedHeadingNode): EnhancedHeadingNode {
    const {tag, collapsed = false, settings = {}, headingId} = serializedNode;
    const node = $createEnhancedHeadingNode(tag, collapsed, settings, headingId);

    // Register the node in the registry when imported (e.g., from collaboration)
    if (headingId) {
      registerHeadingNode(headingId, node.getKey());
    }

    return node;
  }
}

export function $createEnhancedHeadingNode(
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
  collapsed: boolean = false,
  settings: HeadingSettings = {},
  headingId?: string,
  key?: NodeKey
): EnhancedHeadingNode {
  const node = $applyNodeReplacement(
    new EnhancedHeadingNode(tag, collapsed, settings, headingId, key) as any
  ) as EnhancedHeadingNode;

  // Register the node in the registry when created
  registerHeadingNode(node.getHeadingId(), node.getKey());

  return node;
}

export function $isEnhancedHeadingNode(node: unknown): node is EnhancedHeadingNode {
  return node instanceof EnhancedHeadingNode;
}

// Helper to convert regular HeadingNode to EnhancedHeadingNode
export function $convertToEnhancedHeadingNode(
  headingNode: HeadingNode,
  collapsed: boolean = false,
  settings: HeadingSettings = {},
  headingId?: string
): EnhancedHeadingNode {
  const enhancedNode = $createEnhancedHeadingNode(
    headingNode.getTag(),
    collapsed,
    settings,
    headingId
  );

  // Copy all children
  const children = headingNode.getChildren();
  children.forEach((child) => {
    enhancedNode.append(child);
  });

  // Copy format and style
  enhancedNode.setFormat(headingNode.getFormat() as any);
  enhancedNode.setStyle(headingNode.getStyle());

  return enhancedNode;
}
